import { useEffect, useState } from "react";
import axios from "axios";
import { Crosshair, MessageCircle } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { DISCORD_URL, API } from "@/lib/config";

const GAME_META = {
  "Fortnite": { genre: "Battle Royale" },
  "Valorant": { genre: "Tactical FPS" },
  "Counter-Strike 2": { genre: "Tactical FPS" },
  "Apex Legends": { genre: "Battle Royale" },
  "Warzone": { genre: "Battle Royale" },
  "Rainbow Six Siege": { genre: "Tactical FPS" },
  "Rust": { genre: "Survival" },
  "The Finals": { genre: "Arena FPS" },
  "Overwatch 2": { genre: "Hero Shooter" },
  "Marvel Rivals": { genre: "Hero Shooter" },
};

const FALLBACK_GAMES = Object.keys(GAME_META);

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Games = () => {
  const [games, setGames] = useState(FALLBACK_GAMES);

  useEffect(() => {
    document.title = "Supported Games — Scythe Products";
    axios
      .get(`${API}/games`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) setGames(res.data);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="bg-[#0c0d12] text-[#f0f2f5] antialiased min-h-screen" data-testid="games-page">
      <Navbar />
      <main>
        <section className="pt-16 pb-12 px-6 border-b border-red-500/20">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 font-mono-x text-xs text-[#ff4655] mb-4">
              <span className="w-2 h-2 bg-[#ff4655] blink-dot"></span>
              SUPPORTED TITLES // MODEL REGISTRY
            </div>
            <h1 className="font-display text-5xl md:text-8xl uppercase tracking-tighter leading-[0.9] text-white mb-6">
              Built For The Games<br />
              <span className="text-[#ff4655]">You Actually Play.</span>
            </h1>
            <p className="text-zinc-300 text-lg max-w-2xl leading-relaxed">
              Every Scythe model is game-specific — trained for one title's visuals and the objects it is
              designed to recognize. Model availability per game is confirmed on Discord before you order.
            </p>
          </div>
        </section>

        <section className="py-16 px-6 border-b border-red-500/20">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
            {games.map((name, i) => {
              const meta = GAME_META[name] || { genre: "FPS Title" };
              return (
                <article
                  key={name}
                  data-testid={`game-card-${slug(name)}`}
                  className="group p-6 bg-zinc-900/60 border border-zinc-800 hover:border-[#ff4655]/60 transition-colors flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-mono-x text-[#ff4655] text-xs font-bold">
                        MODEL {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-mono-x text-[10px] uppercase tracking-widest text-zinc-500">
                        {meta.genre}
                      </span>
                    </div>
                    <h2 className="font-display text-4xl uppercase text-white mb-3 group-hover:text-[#ff4655] transition-colors">
                      {name}
                    </h2>
                    <p className="text-zinc-400 text-xs leading-relaxed mb-4">
                      A game-specific Scythe vision model trained on this title's visuals. It analyzes the
                      on-screen scene in real time and marks the objects it was built to recognize.
                    </p>
                    <ul className="space-y-2 text-[11px] text-zinc-400 mb-6">
                      <li className="flex items-center gap-2">
                        <Crosshair className="w-3 h-3 text-[#ff4655] shrink-0" />
                        Real-time on-screen object recognition
                      </li>
                      <li className="flex items-center gap-2">
                        <Crosshair className="w-3 h-3 text-[#ff4655] shrink-0" />
                        Regular weight retraining with game updates
                      </li>
                      <li className="flex items-center gap-2">
                        <Crosshair className="w-3 h-3 text-[#ff4655] shrink-0" />
                        Included with Monthly and Lifetime licenses
                      </li>
                    </ul>
                  </div>
                  <div className="flex items-center justify-between gap-3 border-t border-zinc-800 pt-4">
                    <span className="font-mono-x text-[10px] uppercase tracking-widest text-zinc-500">
                      Availability: confirm on Discord
                    </span>
                    <div className="flex gap-2">
                      <a
                        href={DISCORD_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`game-discord-btn-${slug(name)}`}
                        className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white text-[10px] font-bold uppercase tracking-wider transition-colors flex items-center gap-1.5"
                      >
                        <MessageCircle className="w-3 h-3" /> Check
                      </a>
                      <a
                        href="/#pricing"
                        data-testid={`game-order-btn-${slug(name)}`}
                        className="px-4 py-2 bg-[#ff4655] hover:bg-white hover:text-black text-white text-[10px] font-extrabold uppercase tracking-wider transition-colors"
                      >
                        Order
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Games;
